import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import { ClipboardCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { EmptyState, SkeletonRows } from "@/components/app/Common";
import { StatusChip } from "@/components/app/StatusChip";
import { useAuth } from "@/lib/auth";
import { STAFF } from "@/lib/nav";
import { api, get, errMsg } from "@/lib/api";
import { A_STATUS } from "@/lib/assessments";
import { ReliabilityBadge } from "@/pages/candidates/assessment/ReliabilityBadge";
import { cn } from "@/lib/utils";
import { confirmAction } from "@/components/app/Confirm";
import { AssessmentLinksDialog } from "@/pages/assessments/AssessmentLinksDialog";
import { deleteItem, TrashButton } from "@/lib/deleteItem";

const FILTERS = ["all", "sent", "opened", "in_progress", "completed", "expired"];

const Stat = ({ label, value, testId }) => (
  <div className="panel px-4 py-3" data-testid={testId}><p className="text-xs text-ink-muted">{label}</p><p className="font-display text-2xl font-semibold tabular-nums">{value ?? "—"}</p></div>
);

function CopyLinkAction({ r, onLink }) {
  const go = async () => {
    if (!r.has_link && !(await confirmAction({ title: "Generate a new link?", body: "The previous link will stop working. No email is sent.", entity: r.name, confirmLabel: "Generate new link" }))) return;
    try { onLink({ links: [(await api.post(`/assessments/invites/${r.id}/link`, { rotate: !r.has_link })).data], skipped: [] }); } catch (e) { toast.error(errMsg(e)); }
  };
  return <Button size="sm" variant="ghost" className="h-7" onClick={go} data-testid={`copy-link-${r.id}`}>{r.has_link ? "Copy link" : "Generate new link"}</Button>;
}

function Actions({ r, refresh, onLink }) {
  const act = async (url, body, msg) => { try { await api.post(url, body); toast.success(msg); refresh(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="flex justify-end gap-1">
      {["sent", "opened", "in_progress"].includes(r.status) && <CopyLinkAction r={r} onLink={(l) => { onLink(l); refresh(); }} />}
      {["sent", "opened", "in_progress"].includes(r.status) && <Button size="sm" variant="ghost" className="h-7" onClick={() => act(`/assessments/${r.id}/resend`, {}, "New link sent")} data-testid={`resend-${r.id}`}>Resend</Button>}
      {r.status !== "completed" && r.expires_at && <Button size="sm" variant="ghost" className="h-7" onClick={() => act(`/assessments/${r.id}/extend`, { days: 3 }, "Extended by 3 days")} data-testid={`extend-${r.id}`}>Extend 3d</Button>}
      {r.status === "completed" && r.reliability === "low" && !r.retake_offered && <Button size="sm" variant="outline" className="h-7" onClick={() => act(`/assessments/${r.id}/retake`, {}, "Retake offered")} data-testid={`retake-${r.id}`}>Offer retake</Button>}
      <TrashButton label={`Remove invite for ${r.name}`} testId={`delete-invite-${r.id}`} onClick={() => deleteItem({ title: r.status === "completed" ? "Delete this assessment result?" : "Cancel and remove this invite?", entity: r.name,
        body: r.status === "completed" ? "The candidate's answers and score for this assessment are deleted." : "The candidate's link stops working straight away and any answers saved so far are deleted.",
        url: `/assessments/${r.id}`, success: "Invite removed", onDone: refresh, confirmLabel: "Remove" })} />
    </div>
  );
}

export function InviteTracker({ url, showJob, queryKey }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [filter, setFilter] = useState("all");
  const [links, setLinks] = useState(null);
  const { data, isLoading } = useQuery({ queryKey, queryFn: () => get(url), refetchInterval: 15000 });
  if (isLoading) return <SkeletonRows rows={5} />;
  const s = data.stats;
  const rows = data.rows.filter((r) => filter === "all" || r.status === filter);
  const refresh = () => qc.invalidateQueries({ queryKey });
  return (
    <section className="space-y-4" data-testid="invite-tracker">
      <AssessmentLinksDialog result={links} onClose={() => setLinks(null)} />
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="Invites" value={s.total} testId="stat-total" />
        <Stat label="Completion rate" value={s.completion_rate != null ? `${s.completion_rate}%` : null} testId="stat-completion-rate" />
        <Stat label="Average time" value={s.avg_minutes != null ? `${s.avg_minutes} min` : null} testId="stat-avg-time" />
        <Stat label="Low reliability" value={s.low_reliability} testId="stat-low-reliability" />
      </div>
      <div className="flex flex-wrap gap-1.5">
        {FILTERS.map((f) => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`tracker-filter-${f}`}
            className={cn("rounded-full border px-3 py-1 text-xs transition-colors duration-150", filter === f ? "border-brand bg-brand text-white" : "border-line bg-white text-ink-secondary hover:border-brand/40")}>
            {f === "all" ? "All" : A_STATUS[f]} {f === "all" ? s.total : s.by_status[f]}
          </button>
        ))}
      </div>
      {!rows.length ? <EmptyState testId="tracker-empty" icon={ClipboardCheck} title="No invites here" text="Select shortlisted candidates and choose Send assessment." /> : (
        <div className="panel overflow-x-auto">
          <table className="w-full text-sm" data-testid="tracker-table">
            <thead className="border-b border-line text-left text-xs text-ink-muted"><tr>
              <th className="px-4 py-3 font-medium">Candidate</th>{showJob && <th className="px-4 py-3 font-medium">Job</th>}
              <th className="px-4 py-3 font-medium">Status</th><th className="px-4 py-3 font-medium">Progress</th><th className="px-4 py-3 font-medium">Reliability</th>
              <th className="px-4 py-3 font-medium">Score</th><th className="px-4 py-3 font-medium">Expires</th><th />
            </tr></thead>
            <tbody className="divide-y divide-line">
              {rows.map((r) => (
                <tr key={r.id} data-testid={`tracker-row-${r.id}`}>
                  <td className="px-4 py-3"><Link to={`/app/candidates/${r.candidate_id}/assessment`} className="font-medium text-ink hover:text-brand">{r.name}</Link></td>
                  {showJob && <td className="px-4 py-3 text-ink-secondary">{r.job_title}</td>}
                  <td className="px-4 py-3"><StatusChip status={A_STATUS[r.status]} testId={`tracker-status-${r.id}`} />{r.delivery === "link" && <span className="ml-1.5 text-[11px] text-ink-muted" data-testid={`tracker-delivery-${r.id}`}>via link</span>}</td>
                  <td className="px-4 py-3 tabular-nums text-ink-secondary">{r.progress}%{r.reminders_sent.length > 0 && <span className="ml-1 text-xs text-ink-muted">· {r.reminders_sent.length} reminder(s)</span>}</td>
                  <td className="px-4 py-3">{r.reliability ? <ReliabilityBadge level={r.reliability} /> : <span className="text-ink-muted">—</span>}</td>
                  <td className="px-4 py-3 font-display font-semibold tabular-nums">{r.score ?? r.alignment ?? "—"}</td>
                  <td className="px-4 py-3 text-xs text-ink-muted">{r.status === "completed" ? "—" : r.expires_at ? formatDistanceToNow(new Date(r.expires_at), { addSuffix: true }) : "Never"}</td>
                  <td className="px-4 py-3">{STAFF.includes(user.role) && <Actions r={r} refresh={refresh} onLink={setLinks} />}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
